import { useCallback, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  FieldValues,
  Resolver,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form'

import { ActivityType } from '../../TimeTrackingOverviewWidget/const'
import { TimeRecordDataService } from '../services/timeRecordDataService'
import { TimeRecordSubmissionService } from '../services/timeRecordSubmissionService'
import { LogTimeFormData } from '../types'
import { createValidationSchema } from '../validation/schema'
import { DataClassName } from '@/Data/const'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'
import { TimerecordUser } from '@/Data/TimerecordUser/TimerecordUserDataClass'
import { SchemaAttributes } from '@/Data/typesV2'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { PreFillData } from '@/hooks/useLogTimeDrawer'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'
import { useTeamMembers } from '@/Widgets/TimeTrackingOverviewWidget/hooks/useTeamMembers'

interface UseLogTimeFormProps {
  recordId?: string | null
  preFillData?: PreFillData | null
  currentUserId: string
  isLeader?: boolean
  onSuccess: () => void
  onFailure: () => void
}

export const applyEmployeeBillingRules = (
  data: LogTimeFormData,
  original?: { workDuration: number; billedDuration: number },
): LogTimeFormData => {
  const wasSynced =
    !original || original.workDuration === original.billedDuration
  return {
    ...data,
    billedDuration: wasSynced ? data.workDuration : original.billedDuration,
  }
}

export const useLogTimeForm = ({
  recordId,
  preFillData,
  currentUserId,
  isLeader,
  onSuccess,
  onFailure,
}: UseLogTimeFormProps) => {
  const record = recordId ? Timerecord.get(recordId) : null
  const user = record
    ? TimerecordUser.get(ensureString(record.get('user')))
    : null

  // WhoAmI's `name` is a composed field that carries the salutation
  // ("Frau"/"Herr"), so build the label from the separate name parts instead.
  const currentUser = {
    value: currentUserId,
    label: [WhoAmI.get('givenName'), WhoAmI.get('familyName')]
      .map((part) => ensureString(part))
      .filter(Boolean)
      .join(' '),
  }

  const { members: users } = useTeamMembers(currentUserId)

  const { schema } = getSchema(getSchemaName(DataClassName.TIMERECORD))
  const attrs = getLocalizedSchemaAttributes(
    schema?.get('attributes') as SchemaAttributes,
  )
  const activityTypeEnumValues = attrs?.activityType?.enumValues || []
  const defaultActivityType =
    activityTypeEnumValues[0]?.value || ActivityType.BILLABLE

  // Read all Scrivito data during render so that `connect` can intercept
  // "Content not yet loaded". Otherwise `getInitialValues` run in `useEffect`
  // where there is no Scrivito loading context would throw error.
  const userId = record ? ensureString(record.get('user')) : ''
  const userName = user ? ensureString(user.get('name')) : ''
  const recordTypeOfActivity = record
    ? ensureString(record.get('activityType')) || defaultActivityType
    : defaultActivityType
  const recordOrderId = record ? ensureString(record.get('order')) : ''
  const recordPosition = record ? ensureString(record.get('position')) : ''
  const recordBudget = record ? ensureString(record.get('budget')) : ''
  const recordedAt = record ? ensureString(record.get('recordedAt')) : ''
  const recordClient = record ? ensureString(record.get('client')) : ''
  const recordTotalDuration = record
    ? Number(record.get('totalDuration')) || 0
    : 0
  const recordBilledDuration = record
    ? Number(record.get('billedDuration')) || 0
    : 0
  const recordDescription = record
    ? ensureString(record.get('description'))
    : ''
  const recordInternalNote = record
    ? ensureString(record.get('internalNote'))
    : ''

  const positionsForRecord = recordOrderId
    ? TimeRecordDataService.getPositions(recordOrderId)
    : []
  const matchedRow = positionsForRecord.find(
    (p) =>
      p.position === recordPosition &&
      (recordBudget ? p.budget === recordBudget : !p.budget),
  )
  const resolvedWorkPackageId = matchedRow?.id || recordPosition

  const getInitialValues = useCallback((): LogTimeFormData => {
    if (!recordId || !userId || !userName) {
      return {
        users: [{ label: currentUser.label, value: currentUser.value }],
        date: preFillData?.date || new Date().toISOString(),
        customer: preFillData?.customer || '',
        order: preFillData?.order || '',
        workPackage: preFillData?.workPackage || '',
        workPackagePosition: preFillData?.workPackage || '',
        workPackageBudget: undefined,
        workDuration: 0,
        billedDuration: 0,
        typeOfActivity: defaultActivityType,
        productType: preFillData?.productType || '',
        description: preFillData?.description || '',
        internalNote: '',
        toggledTasks: [],
      }
    }

    return {
      users: [{ label: userName, value: userId }],
      date: recordedAt || new Date().toISOString(),
      customer: recordClient,
      order: recordOrderId,
      workPackage: resolvedWorkPackageId,
      workPackagePosition: recordPosition,
      workPackageBudget: recordBudget || undefined,
      workDuration: recordTotalDuration,
      billedDuration: recordBilledDuration,
      typeOfActivity: recordTypeOfActivity,
      productType: '',
      description: recordDescription,
      internalNote: recordInternalNote,
      toggledTasks: [],
    }
  }, [
    recordId,
    currentUser.label,
    currentUser.value,
    preFillData,
    defaultActivityType,
    userName,
    userId,
    recordedAt,
    recordClient,
    recordOrderId,
    resolvedWorkPackageId,
    recordPosition,
    recordBudget,
    recordTotalDuration,
    recordBilledDuration,
    recordTypeOfActivity,
    recordDescription,
    recordInternalNote,
  ])

  const methods = useForm<LogTimeFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(
      createValidationSchema(isLeader),
    ) as unknown as Resolver<LogTimeFormData, FieldValues>,
    defaultValues: getInitialValues(),
  })

  useEffect(() => {
    if (recordId && userId && userName) {
      methods.reset(getInitialValues())
    }
  }, [recordId, userId, userName, getInitialValues, methods])

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    control,
    trigger,
  } = methods

  const selectedCustomerId = useWatch({ control, name: 'customer' })
  const selectedOrderId = useWatch({ control, name: 'order' })

  const onSubmit: SubmitHandler<LogTimeFormData> = async (data) => {
    const original = recordId
      ? {
          workDuration: recordTotalDuration,
          billedDuration: recordBilledDuration,
        }
      : undefined
    const payload = isLeader ? data : applyEmployeeBillingRules(data, original)
    try {
      if (recordId) {
        await TimeRecordSubmissionService.updateTimeRecord(recordId, payload)
      } else {
        await TimeRecordSubmissionService.submitTimeRecord(payload)
      }
      onSuccess()
    } catch (error) {
      console.error('Failed to submit time record:', error)
      onFailure()
    }
  }

  const handleSaveClick = () => {
    handleSubmit(onSubmit)()
  }

  return {
    methods,
    handleSaveClick,
    isSubmitting,
    isDirty,
    trigger,
    selectedCustomerId,
    selectedOrderId,
    users,
  }
}
